import { useRef, useState } from 'react';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';
import { SxProps, Typography, Theme } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ReactComponent as ArrowDownIcon } from '../../assets/arrow-down-expand.svg';
import Button from '../Button';
import { Flex } from '../Flex';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { pluralizeString } from '../../utils/common';
import { ComponentSize } from '../../types/types';
import { radius, shadow, space } from '../../style/utils';

const Wrapper = styled(Flex)`
  position: relative;
`;

const CalendarWrapper = styled(Flex)<{
  $menuHorizontalAnchor: 'left' | 'right';
}>`
  background: ${({ theme }) => theme.palette.surface.base};
  position: absolute;
  top: 50px;
  ${({ $menuHorizontalAnchor }) =>
    $menuHorizontalAnchor === 'left' && 'right: 0px;'};
  border: 1px solid ${({ theme }) => theme.palette.border.default};
  border-radius: ${radius('lg')};
  box-shadow: ${shadow('md')};
  z-index: 3;
`;

const DatePickerButtonWrapper = styled(Button)<{
  $size: ComponentSize;
}>`
  width: inherit;
  justify-content: space-between;
  gap: ${space('sm')};
  align-items: center;
  cursor: pointer;
  svg > path {
    fill: ${({ theme }) => theme.palette.icon.tertiary};
  }

  ${({ $size, theme }) =>
    $size === 'large' &&
    `
    height: 40px;
    padding: ${theme.tokens.space.md} ${theme.tokens.space.lg};
  `}
`;

type DatePickerButtonProps = {
  date?: Dayjs;
  onClick: () => void;
  size?: ComponentSize;
  disabled?: boolean;
};

const DatePickerButton = ({
  date,
  onClick,
  size = 'medium',
  disabled,
}: DatePickerButtonProps) => {
  const daysBack = dayjs().diff(date, 'days');

  return (
    <DatePickerButtonWrapper
      onClick={onClick}
      variant="outlined"
      color="primary"
      $size={size}
      disabled={disabled}
    >
      <Typography>
        {`Last ${pluralizeString(daysBack, 'day', 'days')}`}
      </Typography>
      <ArrowDownIcon width={16} />
    </DatePickerButtonWrapper>
  );
};

export type Props = {
  sx?: SxProps<Theme>;
  value?: dayjs.Dayjs;
  onChange: (val?: dayjs.Dayjs) => void;
  menuHorizontalAnchor?: 'left' | 'right';
  minDate?: dayjs.Dayjs | undefined;
  maxDate?: dayjs.Dayjs | undefined;
  size?: ComponentSize;
  disabled?: boolean;
};

const DaysBackPicker = ({
  sx,
  value,
  onChange,
  menuHorizontalAnchor = 'left',
  minDate,
  maxDate = dayjs(),
  size = 'medium',
  disabled,
}: Props) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const calendarRef = useRef<any>();
  useOnClickOutside(calendarRef, () => setIsDatePickerOpen(false));

  return (
    <Wrapper sx={sx}>
      <DatePickerButton
        date={value}
        onClick={() => setIsDatePickerOpen(true)}
        size={size}
        disabled={disabled}
      />

      {isDatePickerOpen && (
        <CalendarWrapper
          ref={calendarRef}
          $menuHorizontalAnchor={menuHorizontalAnchor}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={value}
              disableFuture
              minDate={minDate}
              maxDate={maxDate}
              onChange={(val) => {
                setIsDatePickerOpen(false);
                onChange(val || undefined);
              }}
            />
          </LocalizationProvider>
        </CalendarWrapper>
      )}
    </Wrapper>
  );
};

export default DaysBackPicker;
